import React from 'react'
import { _getNumberToCurrency } from '../../../Helpers/Functions';
import { Button } from '../../../Components';

function PricingCard({ onClick = null, loading = false, disabled = false, ...plan }) {
    const planLabel = plan?.title || plan?.name || "";
    const isPremium = planLabel.toLowerCase().includes('premium');
    const price = Number(plan.price || 0);
    const hasBackendDiscount = Object.prototype.hasOwnProperty.call(plan, 'discount_type');
    const fallbackDiscount = isPremium ? 40 : 35;
    const rawCompareAtPrice = Number(plan.compare_at_price || plan.mrp || plan.original_price || 0);
    const compareAtPrice = rawCompareAtPrice > price
        ? rawCompareAtPrice
        : hasBackendDiscount
            ? 0
            : Math.ceil((price / (1 - (fallbackDiscount / 100))) / 100) * 100;
    const discountPercent = plan.discount_percent || (compareAtPrice > price
        ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
        : fallbackDiscount);
    const hasDiscount = compareAtPrice > price && discountPercent > 0;

    const cardTone = isPremium
        ? {
            border: 'border-[#FFD21E]/90',
            glow: 'shadow-[0_22px_80px_rgba(255,210,30,0.18)]',
            badge: 'from-[#FFE978] to-[#F0B90B]',
            accent: 'text-[#FFD21E]',
            ring: 'ring-[#FFD21E]/30',
            button: '!bg-[#FFD21E] hover:!bg-[#E8BD00]',
            eyebrow: 'Most Value',
        }
        : {
            border: 'border-white/70',
            glow: 'shadow-[0_20px_70px_rgba(255,255,255,0.08)]',
            badge: 'from-white to-[#D9DEE9]',
            accent: 'text-white',
            ring: 'ring-white/20',
            button: '!bg-white hover:!bg-[#E8ECF5]',
            eyebrow: 'Starter Choice',
        };

    return (
        <div className={`group relative min-h-full overflow-hidden rounded-[1.5rem] border ${cardTone.border} bg-[#0B0E17] text-white font-urbanist ${cardTone.glow} transition duration-300 hover:-translate-y-1`}>
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.16),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.06),transparent_45%)]" />
            <div className={`pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent ${isPremium ? 'via-[#FFD21E]' : 'via-white'} to-transparent opacity-80`} />

            <div className="relative flex h-full flex-col p-5 sm:p-7">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <p className={`mb-2 inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] ${cardTone.accent}`}>
                            {cardTone.eyebrow}
                        </p>
                        <h3 className="text-2xl font-extrabold leading-tight text-white">{plan.name}</h3>
                    </div>
                    {hasDiscount && (
                        <div className={`shrink-0 rounded-full bg-gradient-to-br ${cardTone.badge} px-3 py-2 text-center text-black shadow-lg ring-4 ${cardTone.ring}`}>
                            <div className="text-xl font-black leading-none">{discountPercent}%</div>
                            <div className="text-[10px] font-extrabold uppercase leading-none">Off</div>
                        </div>
                    )}
                </div>

                <div className="mt-7 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
                        <h2 className={`text-[42px] font-black leading-none sm:text-[48px] ${cardTone.accent}`}>
                            {_getNumberToCurrency(plan.price, false, true)}
                        </h2>
                        <div className="pb-1">
                            {hasDiscount && (
                                <div className="text-sm font-semibold text-gray-400 line-through">
                                    {_getNumberToCurrency(compareAtPrice, false, true)}
                                </div>
                            )}
                            <div className="text-xs font-semibold text-gray-300">
                                / {plan.duration_months} Months
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {hasDiscount && (
                            <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-200">
                                Save {_getNumberToCurrency(compareAtPrice - price, false, true)}
                            </span>
                        )}
                        <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-gray-200">
                            Limited membership pricing
                        </span>
                    </div>
                </div>

                <p className="mt-4 min-h-[42px] text-sm leading-6 text-gray-300">{plan.description}</p>

                <div className="my-6 h-px border-t border-dashed border-white/25"></div>

                <div className="flex-1 space-y-3 text-[14px]">
                    {plan.features.map((feature, idx) => (
                        <div key={idx} className={`flex items-start gap-3 rounded-xl px-2 py-1.5 ${feature.status ? 'bg-white/[0.03]' : 'bg-red-500/[0.04]'}`}>
                            <img className="mt-0.5 h-6 w-6 shrink-0" src={`/assets/membership-plans/${feature.status ? 'star-check' : 'star-times'}.png`} alt="" />
                            <div className={`text-base font-semibold leading-6 ${feature.status ? 'text-white' : 'text-gray-400'}`}>{feature.name}</div>
                        </div>
                    ))}
                </div>

                <div className='pt-8'>
                    <Button
                        onClick={onClick}
                        loading={loading}
                        disabled={disabled}
                        width='w-full'
                        className={`${cardTone.button} !rounded-full !py-4 text-base font-extrabold shadow-[0_16px_34px_rgba(0,0,0,0.25)] transition-all group-hover:shadow-[0_18px_42px_rgba(245,196,0,0.18)]`}
                    >
                        Join Now
                    </Button>
                </div>
            </div>
        </div>
    )
}
export { PricingCard };
